import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file TableField.tsx
 * @description `table` 字段類型的可編輯多行表格渲染器。
 *
 * 數據形狀（與 validators.ts 的 `z.array(z.record(...))` 對齊）：
 *   value = Array<Record<columnId, unknown>>，每行一個 record，key 為 column id。
 *
 * 功能：
 *  - 按 schema 的 columns 渲染表頭與單元格控件：text → Input、number → InputNumber、
 *    date → DatePicker（存 'YYYY-MM-DD' 字符串，與其他 date 字段一致）、
 *    signature → 格內手寫簽名（複用 SignatureCapture，每格值存 fileId(number)）
 *  - 行動態增刪：尾部「新增一行」按鈕（達 maxRows 上限時禁用）、每行刪除按鈕
 *  - disabled（唯讀）模式：渲染純文本表格（簽名列顯示縮圖 / 已簽），不出現增刪按鈕
 *
 * 之所以獨立成組件：FieldFactory 的 switch 分支內不能直接使用 hooks（見 FileFieldUpload
 * 的同款註釋）；且表格渲染邏輯較長，收斂於此便於單獨維護。
 *
 * uploadHandlers：signature 列的手寫簽名需上傳為附件文件，故 FieldFactory 需把 uploadHandlers
 * 透傳進來；未傳時 signature 列的簽名入口禁用（與 file/signature 欄位一致）。
 */
import { Button, DatePicker, Input, InputNumber, Table, Typography } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import { memo, useCallback } from 'react';
import { SignatureCapture } from './SignatureField';
/** 把未知 value 規整為行數組（非法形狀一律視為空表） */
function normalizeRows(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((r) => !!r && typeof r === 'object');
}
/** 單元格值轉顯示文本：標量直出，object（不應出現）JSON 化避免 [object Object] */
function cellText(v) {
    if (v == null)
        return '';
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint')
        return String(v);
    return JSON.stringify(v) ?? '';
}
export const TableField = memo(function TableField({ field, value, onChange, disabled, uploadHandlers, }) {
    const rows = normalizeRows(value);
    const maxRows = field.maxRows ?? 100;
    /** 更新某行某列的值（整份 value 淺拷貝後寫回，保持不可變更新語義） */
    const updateCell = useCallback((rowIndex, columnId, cellValue) => {
        const next = normalizeRows(value).map((r, i) => i === rowIndex ? { ...r, [columnId]: cellValue } : r);
        onChange(next);
    }, [value, onChange]);
    const addRow = useCallback(() => {
        onChange([...normalizeRows(value), {}]);
    }, [value, onChange]);
    const removeRow = useCallback((rowIndex) => {
        onChange(normalizeRows(value).filter((_, i) => i !== rowIndex));
    }, [value, onChange]);
    /** 單元格控件：按列類型分發；唯讀時渲染純文本（簽名列例外，仍渲染縮圖 / 已簽） */
    const renderCell = (col, row, rowIndex) => {
        const cell = row[col.id];
        // 簽名列：不論編輯 / 唯讀都交給 SignatureCapture（唯讀時 disabled 顯示縮圖 / 已簽，隱藏採集按鈕）
        if (col.type === 'signature') {
            return (_jsx(SignatureCapture, { fileId: typeof cell === 'number' ? cell : null, onChange: (next) => updateCell(rowIndex, col.id, next ?? undefined), disabled: disabled, uploadHandlers: uploadHandlers, filePrefix: `signature-${field.id}-${rowIndex}-${col.id}`, compact: true }));
        }
        if (disabled) {
            return _jsx("span", { children: cellText(cell) });
        }
        switch (col.type) {
            case 'number':
                return (_jsx(InputNumber, { size: "small", style: { width: '100%' }, value: typeof cell === 'number' ? cell : undefined, onChange: (v) => updateCell(rowIndex, col.id, v ?? undefined) }));
            case 'date':
                return (_jsx(DatePicker, { size: "small", style: { width: '100%' }, value: typeof cell === 'string' && cell ? dayjs(cell) : undefined, onChange: (d) => updateCell(rowIndex, col.id, d ? d.format('YYYY-MM-DD') : undefined) }));
            case 'text':
            default:
                return (_jsx(Input, { size: "small", value: typeof cell === 'string' ? cell : cellText(cell), onChange: (e) => updateCell(rowIndex, col.id, e.target.value) }));
        }
    };
    const columns = field.columns.map((col) => ({
        title: col.label,
        key: col.id,
        render: (_, row, rowIndex) => renderCell(col, row, rowIndex),
    }));
    // 編輯態尾列：刪除行按鈕
    if (!disabled) {
        columns.push({
            title: '',
            key: '__actions',
            width: 48,
            render: (_, __, rowIndex) => (_jsx(Button, { type: "text", size: "small", danger: true, "aria-label": "\u522A\u9664\u6B64\u884C", icon: _jsx(Trash2, { size: 14 }), onClick: () => removeRow(rowIndex) })),
        });
    }
    return (_jsxs("div", { children: [_jsx(Table, { size: "small", rowKey: (_, i) => String(i), columns: columns, dataSource: rows, pagination: false, locale: { emptyText: '（無資料）' } }), !disabled && (_jsxs("div", { style: { marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx(Button, { size: "small", icon: _jsx(Plus, { size: 14 }), disabled: rows.length >= maxRows, onClick: addRow, children: "\u65B0\u589E\u4E00\u884C" }), field.minRows != null && rows.length < field.minRows && (_jsxs(Typography.Text, { type: "secondary", style: { fontSize: 12 }, children: ["\u5EFA\u8B70\u81F3\u5C11\u586B\u5BEB ", field.minRows, " \u884C"] }))] }))] }));
});
//# sourceMappingURL=TableField.js.map